import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Building2, Home, Users, Check, AlertCircle, MapPin } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ENTITY_OPTIONS = [
  {
    id: "center",
    label: "Childcare Center",
    icon: Building2,
    desc: "Larger licensed center, typically commercial building, no child-count cap",
  },
  {
    id: "group_home",
    label: "Group Child Care Home",
    icon: Users,
    desc: "Mid-size operation — can be commercial or residential, limited children",
  },
  {
    id: "family_home",
    label: "Family / In-Home Daycare",
    icon: Home,
    desc: "Small operation run from operator's residence (IRS Pub 587 / Form 8829)",
  },
];

const LOCATION_OPTIONS = [
  {
    id: "commercial",
    label: "Dedicated commercial building",
    icon: Building2,
    desc: "Building is used 100% for the business (no personal residence use)",
  },
  {
    id: "residence",
    label: "My residence / home",
    icon: Home,
    desc: "Operates from the home — Time-Space % applies (IRS daycare exemption)",
  },
];

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

/**
 * One-time entity setup wizard. Shows after the disclaimer modal on the first
 * post-login experience for users who don't yet have entity_type set.
 * Saves to /api/onboarding/entity-setup which determines the allocation method
 * used across the CACFP toolkit.
 */
export default function OnboardingWizard() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [entityType, setEntityType] = useState("");
  const [locationType, setLocationType] = useState("");
  const [entityState, setEntityState] = useState("");
  const [cacfpParticipation, setCacfpParticipation] = useState("");  // "active" | "pending" | "none"
  const [cacfpFyMode, setCacfpFyMode] = useState("calendar");          // "calendar" | "usda_fy"
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Total step count depends on CACFP answer — only "active" needs Step 5 (FY mode).
  const totalSteps = cacfpParticipation === "active" ? 5 : 4;

  useEffect(() => {
    if (!user) return;
    if (!user.disclaimer_accepted_at) return; // wait for disclaimer first
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/onboarding/status`, { withCredentials: true });
        if (active && !data.entity_setup_done) setOpen(true);
      } catch { /* keep closed on error */ }
    })();
    return () => { active = false; };
  }, [user]);

  const isInvalidCombo = entityType === "family_home" && locationType === "commercial";

  const handleSubmit = async () => {
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/onboarding/entity-setup`, {
        entity_type: entityType,
        location_type: locationType,
        entity_state: entityState || null,
        cacfp_participation: cacfpParticipation || "none",
        cacfp_fy_mode: cacfpParticipation === "active" ? cacfpFyMode : null,
      }, { withCredentials: true });
      // Pull fresh user record so the React AuthContext picks up entity_type /
      // location_type / allocation_method + the new CACFP fields. Without this,
      // CACFP guards keep firing until the user logs out and back in.
      try { await refreshUser(); } catch {/* non-fatal — modal still closes */}
      setOpen(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Setup failed — please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4"
      data-testid="onboarding-wizard"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 bg-purple-50 border-b border-purple-100">
          <h2 className="text-lg font-extrabold text-purple-900" style={{ fontFamily: "Manrope" }}>
            Quick Setup — Step {step} of {totalSteps}
          </h2>
          <p className="text-xs text-purple-700 mt-1">
            We'll use this to tailor your CACFP allocation calculations correctly.
          </p>
          {/* Stepper */}
          <div className="flex gap-1.5 mt-3">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((n) => (
              <div
                key={n}
                className={`h-1 flex-1 rounded-full ${n <= step ? "bg-purple-600" : "bg-purple-200"}`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 overflow-y-auto">
          {step === 1 && (
            <div className="space-y-3" data-testid="wizard-step-1">
              <p className="text-sm font-bold text-slate-900 mb-3">How is your operation licensed?</p>
              {ENTITY_OPTIONS.map(({ id, label, icon: Icon, desc }) => (
                <button
                  key={id}
                  onClick={() => setEntityType(id)}
                  data-testid={`entity-${id}`}
                  className={`w-full flex items-start gap-3 text-left rounded-lg border-2 px-4 py-3 transition-all ${
                    entityType === id ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${entityType === id ? "text-purple-600" : "text-slate-500"}`} />
                  <div>
                    <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                  {entityType === id && <Check className="w-4 h-4 text-purple-600 ml-auto flex-shrink-0" />}
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <p className="text-sm font-bold text-slate-900 mb-3">Where do you actually operate from?</p>
              {LOCATION_OPTIONS.map(({ id, label, icon: Icon, desc }) => (
                <button
                  key={id}
                  onClick={() => setLocationType(id)}
                  data-testid={`location-${id}`}
                  className={`w-full flex items-start gap-3 text-left rounded-lg border-2 px-4 py-3 transition-all ${
                    locationType === id ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${locationType === id ? "text-purple-600" : "text-slate-500"}`} />
                  <div>
                    <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                  {locationType === id && <Check className="w-4 h-4 text-purple-600 ml-auto flex-shrink-0" />}
                </button>
              ))}
              {isInvalidCombo && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mt-2" data-testid="wizard-invalid-combo">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">
                    A Family/In-Home Daycare cannot operate from a dedicated commercial property. Please go back and choose Childcare Center or Group Home.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="wizard-step-3">
              <p className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> What state do you operate in?
              </p>
              <p className="text-xs text-slate-500 mb-3">
                We'll show your state's specific employer childcare tax credit (if available) plus the federal IRS Form 8882 credit.
              </p>
              <select
                value={entityState}
                onChange={(e) => setEntityState(e.target.value)}
                data-testid="entity-state-select"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select your state…</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                <p className="text-xs text-blue-900">
                  <strong>Summary:</strong> {ENTITY_OPTIONS.find(e => e.id === entityType)?.label} operating from a {LOCATION_OPTIONS.find(l => l.id === locationType)?.label.toLowerCase()}.
                </p>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="wizard-step-4">
              <p className="text-sm font-bold text-slate-900 mb-1">Are you a CACFP (food program) participant?</p>
              <p className="text-xs text-slate-500 mb-3">
                If you receive USDA reimbursements through your state's CACFP sponsor, choose "Active". You can change this any time in Settings.
              </p>
              {[
                { id: "active",  label: "Yes — actively enrolled",      desc: "You receive monthly CACFP reimbursements" },
                { id: "pending", label: "Application pending",          desc: "You've applied but are not yet approved" },
                { id: "none",    label: "No — not in CACFP",            desc: "You don't participate in the food program" },
              ].map(({ id, label, desc }) => (
                <button
                  key={id}
                  onClick={() => setCacfpParticipation(id)}
                  data-testid={`cacfp-${id}`}
                  className={`w-full flex items-start gap-3 text-left rounded-lg border-2 px-4 py-3 transition-all ${
                    cacfpParticipation === id ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                  {cacfpParticipation === id && <Check className="w-4 h-4 text-purple-600 ml-auto flex-shrink-0" />}
                </button>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3" data-testid="wizard-step-5">
              <p className="text-sm font-bold text-slate-900 mb-1">What's your CACFP accounting year?</p>
              <p className="text-xs text-slate-500 mb-3">
                FNS Instruction 796-2 Rev. 4 lets operators choose, as long as the choice is consistent. Most family-home and small-center operators use Calendar Year because it matches IRS Schedule C.
              </p>
              {[
                { id: "calendar", label: "Calendar Year (Jan 1 – Dec 31)",       desc: "Recommended — matches IRS Schedule C", badge: "Most common" },
                { id: "usda_fy",  label: "USDA Federal FY (Oct 1 – Sept 30)",    desc: "USDA reporting standard", badge: null },
              ].map(({ id, label, desc, badge }) => (
                <button
                  key={id}
                  onClick={() => setCacfpFyMode(id)}
                  data-testid={`cacfp-fy-${id}`}
                  className={`w-full flex items-start gap-3 text-left rounded-lg border-2 px-4 py-3 transition-all ${
                    cacfpFyMode === id ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{label}</p>
                      {badge && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">{badge}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                  {cacfpFyMode === id && <Check className="w-4 h-4 text-purple-600 ml-auto flex-shrink-0" />}
                </button>
              ))}
              <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                💡 You can change this later in Settings, but if you've already entered CACFP data we'll warn you before re-bucketing.
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="wizard-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
          <button
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1 || busy}
            data-testid="wizard-back-btn"
            className="text-sm font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed px-4 py-2"
          >
            ← Back
          </button>
          {step < totalSteps && (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && !entityType) ||
                (step === 2 && (!locationType || isInvalidCombo)) ||
                (step === 3 && !entityState) ||
                (step === 4 && !cacfpParticipation)
              }
              data-testid="wizard-next-btn"
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold px-6 py-2 rounded-lg transition-all"
            >
              Next →
            </button>
          )}
          {step === totalSteps && (
            <button
              onClick={handleSubmit}
              disabled={busy || (step === 5 && !cacfpFyMode)}
              data-testid="wizard-finish-btn"
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white text-sm font-bold px-6 py-2 rounded-lg transition-all"
            >
              {busy ? "Saving…" : "Finish Setup"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
